/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./postProcess/QueryPostProcessHelper',
	'./api/DataQueryExecutionAPI',
	'./api/InternalDataQueryExecutionAPI',
	'./api/QueryResultsAPI',
	'./QueryResults',
	'./DataQueryResult',
	'../../dashboard/prompts/PromptsState',
	'underscore'
], (APIFactory, QueryPostProcessHelper, DataQueryExecutionAPI, InternalDataQueryExecutionAPI, QueryResultsAPI, QueryResults, DataQueryResult, PromptsState, _) => {

	class DataQueryExecution {

		MAIN = 'main';
		static QUERY_LANGUAGE_PROP_NAMES = { TYPE: 'type', QUERY_LANGUAGE: 'queryLanguage' };
		static COGNOS_QUERY_LANGUAGE = ['cognosSQL'];
		static NATIVE_QUERY_LANGUAGE = ['SQL', 'nativeSQL'];
		static MDX_QUERY_LANGUAGE = ['MDX'];

		constructor({ dashboardAPI, content, features, options }) {
			this.options = options || {};
			this.features = features;
			this.content = content;
			this.dashboard = dashboardAPI;
			this.canvas = this.features['Dashboard.Canvas'];
			this.dashboardSettings = this.features['Dashboard.DashboardSettings'];
			this.logger = this.dashboard.getGlassCoreSvc('.Logger');
			this._queryProviders = {};
			this._queryModifiers = {};
			this._queryDefinitionProviders = {};
			this._queryDefinitionModifiers = {};
			this._queryPostProcessors = [];
			this._queryResults = null;
			this._cachedResults = null;
			this._rawResults = {};
			this._cachedDefinitionList = null;

			this._requestOptions = {};

			// Query key and value pair with the Key constructed from query spec and last modified time stamp, and the value
			// used to uniquely identify the query result data when render viz.
			this._queryKeyValuePair = {};

			this._promptsState = new PromptsState();
		}

		initialize() {
			// pre-execute is turned on by default.
			// Turn off pre-execute only when explicitly turned off in the feature spec
			if (this.options.preExecuteOnLoad !== false && !this.dashboardSettings.get('disableQueryPreExecution')) {
				const visualization = this._getVisualization();
				const datasource = visualization.getDataSource();
				if (datasource) {
					const canvasContent = this.canvas.getContent();
					const canvasState = canvasContent.getFeature('state');
					const state = this.features['state'];
					Promise.all([
						datasource.loadMetadata(),
						canvasState.whenStatusChanges(state.STATUS.INITIALIZED),
						state.whenStatusChanges(state.STATUS.INITIALIZED)
					]).then(this._preExecuteQuery.bind(this));
					// Note that we intentionally use initialize here even though this could be done in the constructor
					// in order to force creation of this feature to start data queries early. It is also intended not to 
					// wait for it to be completed as the render sequence will do that anyway.
				}
			}
		}

		getAPI(type) {
			if(type === 'internal') {
				if(!this.internalAPI) {
					this.internalAPI = APIFactory.createAPI(this, [DataQueryExecutionAPI, InternalDataQueryExecutionAPI]);
				}
				return this.internalAPI;
			} else {
				if (!this.api) {
					this.api = APIFactory.createAPI(this, [DataQueryExecutionAPI]);
				}
				return this.api;
			}
		}

		destroy() {
			this.options = null;
			this.content = null;
			this.dashboard = null;
			this.canvas = null;
			this.features = null;
			this.logger = null;
			this._queryProviders = null;
			this._queryModifiers = null;
			this._queryDefinitionProviders = null;
			this._queryDefinitionModifiers = null;
			this._rawResults = null;
			this._cachedResults = null;
			this._cachedDefinitionList = null;
			this.api = null;

			if (this._queryResults) {
				this._queryResults.destroy();
				this._queryResults = null;
			}

			this._promptsState.destroy();
			this._promptsState = null;
		}

		_preExecuteQuery() {
			// Top level container content type may differ between applications
			// While dashboard top container content type is 'page', explore has 'exploreCard' as the content type
			const pages = this.canvas.getContent().getChildren();
			const selectedPages = pages && pages.length ? this.canvas.getSelectedContentList({ type: pages[0].getType() }): [];
			// Pre-execute content queries for ONLY the first page
			if (selectedPages && selectedPages.length && selectedPages[0].getContent(this.content.getId())) {
				this._preExecuteCacheReady = this.executeQueries().then(result => {
					this.setResultsCache(result);
					return true;
				});
			}
		}

		hasResultsCache() {
			return this._preExecuteCacheReady ? this._preExecuteCacheReady : Promise.resolve(false);
		}

		setResultsCache(results) {
			this._cachedResults = results;
		}

		_clearResultsCache() {
			this._cachedResults = null;
			this._preExecuteCacheReady = null;
		}

		getResultsCache() {
			const results = this._cachedResults;
			this._clearResultsCache();
			return results;
		}

		addRequestOptions(options = {}) {
			//Unlike other options, "adding" to the qfb option 
			//means merging into a comma-separated, unique string of options.
			if (options.qfb && this._requestOptions && this._requestOptions.qfb) {
				options.qfb = _.union(this._requestOptions.qfb.split(','), options.qfb.split(',')).join(',');
			}
			this._requestOptions = {
				...this._requestOptions,
				...options
			};
		}

		removeRequestOptions(options = []) {
			let key;
			for(let i = 0; i < options.length; i++) {
				key = options[i];
				if (this._requestOptions[key]) {
					delete this._requestOptions[key];
				}
			}
		}

		resetRequestOptions() {
			this._requestOptions = {};
		}

		_isValidOption( name ) {
			return this._requestOptionWhiteList && this._requestOptionWhiteList.includes(name);
		}

		registerQueryProvider(provider) {
			if (provider && provider.getQuerySpecList) {
				const type = provider.getType();
				if (!this._queryProviders[type]) {
					this._queryProviders[type] = [];
				}
				this._queryProviders[type].push(provider);
			}
		}

		registerQueryDefinitionProvider(provider) {
			if(!provider.getQueryDefinitionList) {
				throw new Error('Invalid QueryDefinitionProvider');
			}
			const type = provider.getType();
			if (!this._queryDefinitionProviders[type]) {
				this._queryDefinitionProviders[type] = [];
			}
			this._queryDefinitionProviders[type].push(provider);
		}

		getCurrentQueryResults() {
			let results = this._queryResults && this._queryResults.getAPI();
			if (!results) {
				// @todo temporary solution to obtain the query results when the query
				// was not executed by the new DataQueryExecution
				const renderSequence = this.features['RenderSequence.internal'];
				results = renderSequence.getCurrentRenderContext('data').getData('data');
			}
			return results;
		}

		getRawResult(id = 'data') {
			return this._rawResults && this._rawResults[id];
		}
		/**
		 * Add a spec with max min value as min max query results to the current query results
		 * @param {object} spec - includes data items and min max info
		 * Note - a falsy spec will remove the existing min max query results
		 * eg.
		 * {
		 * 		reveuneavg: {
		 * 			min: 20,
		 * 	 	 	max: 100,
		 * 		 	columnId: 'revenue',
		 * 			layerId: 'data'
		 *  	},
		 * 		quantitysum: {
		 * 			min: -100,
		 * 	 	 	max: 80,
		 * 		 	columnId: 'quantity',
		 * 			layerId: 'data.region'
		 * 		}
		 * }
		 */
		setMinMaxQueryResults(spec) {
			if (spec) {
				const layersData = {};
				_.each(spec, (dataItemValue, dataItemKey) => {
					const layerId = dataItemValue.layerId;
					const dataItem = {
						itemClass: {
							h: [{
								u: dataItemValue.columnId,
								aggregate: dataItemValue.aggregate
							}],
							id: dataItemKey
						}
					};
					if (layersData[layerId]) {
						layersData[layerId].pt.push({ v: dataItemValue.min }, { v: dataItemValue.max });
						layersData[layerId].dataItems.push(dataItem, dataItem);
					} else {
						layersData[layerId] = {
							pt: [{ v: dataItemValue.min }, { v: dataItemValue.max }],
							dataItems: [dataItem, dataItem]
						};
					}
				});
				// creates a min max query result per layer
				_.each(layersData, (data, layerId) => {
					const mockData = {
						dataItems: data.dataItems,
						data: [{ pt: data.pt }]
					};
					const queryResult = new DataQueryResult(mockData, this._getVisualization().getSlots());
					this._queryResults.addResult(queryResult, layerId, QueryResultsAPI.QUERY_RESULT_TYPE.MINMAX);
				});
			} else {
				// removes min max queries from all of the layers
				const layerIds = this._queryResults.getQueryResultIdList();
				_.each(layerIds, (layerId) => {
					this._queryResults.removeResult(layerId, QueryResultsAPI.QUERY_RESULT_TYPE.MINMAX);
				});

			}
			return this._queryResults.getAPI();
		}

		registerQueryModifier(modifier) {
			if (modifier && modifier.modifyQuerySpecList) {
				const type = modifier.getType();
				if (!this._queryModifiers[type]) {
					this._queryModifiers[type] = [];
				}
				this._queryModifiers[type].push(modifier);
				return {
					remove: () => this._queryModifiers[type].splice(this._queryModifiers[type].indexOf(modifier), 1)
				};
			}
		}

		registerQueryDefinitionModifier(modifier) {
			if(!modifier.modifyQueryDefinitionList) {
				throw new Error('Invalid QueryDefinitionModifier');
			}
			const type = modifier.getType();
			if (!this._queryDefinitionModifiers[type]) {
				this._queryDefinitionModifiers[type] = [];
			}
			this._queryDefinitionModifiers[type].push(modifier);
			return {
				remove: () => this._queryDefinitionModifiers[type].splice(this._queryDefinitionModifiers[type].indexOf(modifier), 1)
			};
		}

		registerQueryPostProcessor(postProcessor) {
			if (postProcessor) {
				this._queryPostProcessors.push(postProcessor);
				return {
					remove: () => this._queryPostProcessors.splice(this._queryPostProcessors.indexOf(postProcessor), 1)
				};
			}
		}

		executeQueries(type = this.MAIN) {
			if (this._skipDataQuery()) {
				return this._buildEmptyData();
			}

			return this.hasResultsCache().then(cached => {
				if (cached) {
					const cachedResults = this.getResultsCache();
					return this.queryChanged().then(queryHasChanged => {
						// if the query has changed, ignore the cache and execute the query
						// otherwise utilize the cached result
						return queryHasChanged ? this.executeQueries(type) : cachedResults;
					});
				}

				return this._getQueryDefinitionList(type).then(queryDefinitions => {
					// Once the definition list is consumed for query execution
					// make sure to clear the definition cache
					this._setDefinitionListCache();

					//Favour using QueryService2 and its constructs if they support this query
					this._currentV2Specs = null;
					if(queryDefinitions && queryDefinitions.length) {
						return this._executeQueries(queryDefinitions).then(resultList => {
							this.resetRequestOptions();
							this._resetPromptState();
							return resultList;
						});
					}

					//Otherwise fall back to original QueryService
					const querySpecList = this._getQuerySpecList(type);
					if (querySpecList) {
						return this._doExecute(querySpecList, type).then(resultList => {
							this.resetRequestOptions();
							this._resetPromptState();
							return resultList;
						});
					} else {
						return Promise.reject();
					}
				});
			}).catch((e) => {
				this._clearResultsCache();
				throw e;
			});
		}

		_resetPromptState() {
			if (this._promptsState.hasInProgressPrompts()) {
				this._promptsState.reset();
			}
		}

		/**
		 * with the { qfb: 'usedParameterValue' } option, query response will return parameter values used for the query.
		 * Update (clean) the saved prompts accordingly.
		 * e.g.
		 * {
		 * 	'usedParams': [{
		 * 	'parameterName': 'multiPrompt',
		 * 	'dataType': 'string',
		 * 	'modelFilterItem': '[Model query subjects (Sales and Marketing)].[Product].[Product line]',
		 * 	'capabilities': {
		 * 		'unboundedRange': false,
		 * 		'multivalued': true,
		 * 		'boundRange': false,
		 * 		'optional': false,
		 * 		'discreteValue': true
		 * 	},
		 * 	'invert': false,
		 * 	'values': [{
		 * 		'operator': 'equal',
		 * 		'use': 'Golf Equipment',
		 * 		'display': ''
		 * 	}, {
		 * 		'operator': 'equal',
		 * 		'use': 'Mountaineering Equipment',
		 * 		'display': ''
		 * }],
		 * 'location': 'global'
		 * }]
		 * } 
		 */
		_resetSavedPrompts(rawResultList) {
			const usedPromptNames = [];
			let meta;
			rawResultList.forEach(rawResult => {
				// Different structure for v1 and v2 query raw results
				meta = rawResult.meta || rawResult.data.meta;
				if (meta && meta.usedParams && meta.usedParams.length) {
					meta.usedParams.forEach(paramSpec => {
						if (paramSpec.location === 'query' && usedPromptNames.indexOf(paramSpec.parameterName) < 0) {
							usedPromptNames.push(paramSpec.parameterName);
						}
					});
				}
			});

			let newSpecs = null;
			const savedPrompts = this._getVisualization().getSavedPrompts();
			const optionalPrompts = savedPrompts.getPromptSpecList().filter(spec => spec.capabilities.optional);
			if (usedPromptNames.length || optionalPrompts.length) {
				newSpecs = {};
				// Need to get the original prompt spec for member ids to uniquely identify selected members  
				usedPromptNames.forEach(promptName => newSpecs[promptName] = savedPrompts.getPromptSpec(promptName));

				// Add the optional prompts which are not included as used params
				optionalPrompts.forEach(prompt => { newSpecs[prompt.name] = prompt; });
			}
			// Reset to null if prompts are all deleted, otherwise reset to new values.
			savedPrompts.reset(newSpecs);
		}

		_getQuerySpecList(type = this.MAIN) {
			const queryProviderList = this._queryProviders[type];
			const queryModifierList = this._queryModifiers[type];
			if (queryProviderList && queryProviderList.length > 0) {
				let querySpecList = [];
				for( let j = 0; j < queryProviderList.length; j++  ) {
					let provider = queryProviderList[j];
					let specList = provider.getQuerySpecList();
					for( let k=0; k < specList.length; k++) {
						let specEntry = specList[k];
						querySpecList.push(specEntry);
					}
				}
				if (queryModifierList && queryModifierList.length > 0) {
					for( let i = 0; i < queryModifierList.length ; i++ ) {
						let modifier = queryModifierList[i];
						querySpecList = modifier.modifyQuerySpecList(querySpecList);
					}
				}
				return querySpecList;
			} else {
				this.logger.error('Required query queryProvider is not registered');
			}
		}

		_getQueryDefinitionList(type) {
			// use the cached list if available
			const cachedList = this._getDefinitionListCache();
			if (cachedList && cachedList.length) {
				return Promise.resolve(cachedList);
			}

			const allProviders = this._queryDefinitionProviders[type] || [];
			const providers = allProviders.filter(provider => provider.supports(this.content));
			if(providers.length) {
				const modifiers = this._queryDefinitionModifiers[type] || [];

				return Promise.all(providers.map(provider => provider.getQueryDefinitionList())).then(
					(definitionLists) => {
						let definitionList = _.flatten(definitionLists);
						modifiers.forEach(modifier =>
							definitionList = modifier.modifyQueryDefinitionList(definitionList)
						);
						return this._setDefinitionListCache(definitionList);
					}
				);
			}
			return Promise.resolve(null);
		}

		_setDefinitionListCache(definitionList = null) {
			this._cachedDefinitionList = definitionList;
			return this._cachedDefinitionList;
		}

		_getDefinitionListCache() {
			return this._cachedDefinitionList;
		}

		_doExecute(queryList, providerType) {
			const slots = this._getVisualization().getSlots();
			this.currentSpecList = [];
			const requestOptions = this._getRequestOptions();
			for( let i = 0; i <queryList.length; i++ ) {
				const query = queryList[i];
				this.currentSpecList.push(JSON.stringify(query.spec));
				query.result = this.features['Dashboard.QueryService.internal'].executeQuery(
					query.dataSourceId,
					query.spec,
					this.constructor.name,
					requestOptions
				);
			}

			const resultPromises = _.pluck(queryList, 'result');
			return Promise.all(resultPromises)
				.then((rawResultList) => {
					// save the raw result
					this._rawResults = {};
					this._buildQueryTag(queryList, rawResultList);
					this._resetSavedPrompts(rawResultList);

					const queryResults = new QueryResults(this._queryKeyValuePair.value);
					if (providerType === this.MAIN) {
						// cache the main query results
						this._queryResults = queryResults;
					}

					for( let index =0; index<rawResultList.length; index++  ) {
						const rawResult = rawResultList[index];
						const queryId = queryList[index].id;
						const queryType = queryList[index].type;
						// Should be able to just pass mapped data items, but due to DSS defect 274285, temporarily pass slots to build result.
						const resultInstance = new DataQueryResult(rawResult.data, slots);
						if ((providerType === this.MAIN || providerType === 'detail') && queryType === QueryResultsAPI.QUERY_RESULT_TYPE.MAIN) {
							if (providerType === this.MAIN) {
								// Make the query spec and raw response available as properties in the query result data object
								resultInstance.setPropertyValue('QuerySpec.internal', JSON.stringify(queryList[index].spec));
								// Ideally we should access the raw data from the ajax response as raw string,
								// but currentlt we don't have access to it because we query using modelling which we should mve away from
								resultInstance.setPropertyValue('RawData.internal',JSON.stringify(rawResult.data));
								resultInstance.setPropertyValue('RequestTime.internal', rawResult.requestTime || null );

								if(  rawResult.data.meta && rawResult.data.meta.messages ) {
									this._setQueryFeedbackResult(rawResult.data.meta.messages, resultInstance);
								}
								if (rawResult.zipiId) {
									resultInstance.setPropertyValue('ZIPI.id', rawResult.zipiId);
								}

								// Keep the raw data
								this._rawResults[queryId] = rawResult.data;
							}
							const queryDefinition = {
								getId: () => queryId
							};
							// Post process query result data
							this._postProcess(rawResult.data, queryDefinition);
						}

						queryResults.addResult(resultInstance, queryId, queryType);
					}
					return queryResults.getAPI();
				})
				.catch((jqXHR) => {
					return this.features['Dashboard.QueryService.internal.error'].handleQueryError({
						error: jqXHR,
						promptsState: this._promptsState,
						visualization: this._getVisualization(),
						content: this.content
					}).then(promptInfoList => {
						if (promptInfoList && promptInfoList.length) {
							// Rerun queries after resolve the prompts
							queryList.forEach(query => {
								query.spec.parameterValues = promptInfoList;
							});
							return this._doExecute(queryList, providerType);
						}
					});
				});
		}

		_setQueryFeedbackResult(messages, resultInstance) {
			for (let i = 0; i < messages.length; i++) {
				let message;
				message = messages[i];
				let queryLanguagePropName;
				if(message[DataQueryExecution.QUERY_LANGUAGE_PROP_NAMES.QUERY_LANGUAGE]) {
					queryLanguagePropName = DataQueryExecution.QUERY_LANGUAGE_PROP_NAMES.QUERY_LANGUAGE;
				} else if(message[DataQueryExecution.QUERY_LANGUAGE_PROP_NAMES.TYPE]) {
					queryLanguagePropName = DataQueryExecution.QUERY_LANGUAGE_PROP_NAMES.TYPE;
				} else {
					return;
				}
				const queryLanguage = message[queryLanguagePropName];
				if (DataQueryExecution.COGNOS_QUERY_LANGUAGE.indexOf(queryLanguage) !== -1) {
					resultInstance.setPropertyValue('QueryFeedback.cognosSQL', message.message);
				} else if (DataQueryExecution.NATIVE_QUERY_LANGUAGE.indexOf(queryLanguage) !== -1) {
					resultInstance.setPropertyValue('QueryFeedback.nativeSQL', message.message);
				} else if(DataQueryExecution.MDX_QUERY_LANGUAGE.indexOf(queryLanguage) !== -1) {
					resultInstance.setPropertyValue('QueryFeedback.MDX', message.message);
				}
			}
		}

		_getRequestOptions() {
			return this._requestOptions;
		}

		_executeQueries(definitions) {
			const query = this.features['Dashboard.QueryService2'].createQuery(definitions);
			const errorHandler = this.features['Dashboard.QueryService.internal.error'];
			query.registerFaultHandler(errorHandler.handleQueryError.bind(errorHandler, {
				promptsState: this._promptsState,
				visualization: this._getVisualization(),
				content: this.content
			}));
			query.registerPostProcessor(this._postProcess.bind(this));

			// Use this object to cache query results prior to post-processing and saving with the result.
			const rawDataInternalByQueryId = {};
			query.registerSaveRawDataHandler((rawDataResult, queryId) => {
				//Save an object that contains the queryfeedback metadata (if any) and the raw result to save to the RawData.internal property.
				//NOTE: Its important to save string (or cloned) raw results here since post-processors can modify the output.
				rawDataInternalByQueryId[queryId] = { meta: rawDataResult.meta, result: JSON.stringify(rawDataResult) };
			});

			this._currentV2Specs = JSON.stringify(query.getSpecList());

			const requestOptions = this._getRequestOptions();
			return query.execute(undefined, requestOptions).then(queryResultList => {
				this._buildQueryDefinitionTag(query, queryResultList);
				this._resetSavedPrompts(Object.values(rawDataInternalByQueryId));
				const queryResults = new QueryResults(this._queryKeyValuePair.value, query.getWarningList());
				queryResultList.forEach((queryResult, index) => {
					const queryDefinition = definitions[index];
					const queryId = queryDefinition.getId();
					const queryType = queryDefinition.getType();
					const rawResult = rawDataInternalByQueryId[queryId];

					queryResult.setPropertyValue('QuerySpec.internal',this._currentV2Specs);
					if (rawResult) {
						queryResult.setPropertyValue('RawData.internal',rawResult.result);

						if( rawResult.meta && rawResult.meta.messages ) {
							this._setQueryFeedbackResult(rawResult.meta.messages, queryResult);
						}
					}

					queryResults.addResult(queryResult, queryId, queryType);
				});

				return queryResults.getAPI();
			});
		}



		_skipDataQuery() {
			const definition = this._getVisualization().getDefinition();
			return !!definition.getProperty('noDataQuery');
		}

		_buildEmptyData() {
			const slots = this._getVisualization().getSlots();
			const mappedInfo = slots.getMappingInfoList();
			const dataItemList = _.pluck(mappedInfo, 'dataItem');
			const emptyData = {
				dataItems: _.map(dataItemList, (dataItem) => {
					let emptyDataItem = {
						itemClass: {
							h: [{
								u: dataItem.getColumnId(),
								d: dataItem.getLabel()
							}],
							id: dataItem.getId()
						}
					};
					// ordinal dataitems requires an aggregation
					// while categorical dataitems requires an empty items array
					const aggregate = dataItem.getAggregation();
					if (!dataItem.hasDefaultAggregation() && aggregate) {
						emptyDataItem.itemClass.h[0].aggregate = aggregate;
					} else {
						emptyDataItem.items = [];
					}
					return emptyDataItem;
				}),
				data:[]
			};
			const queryResult = new DataQueryResult(emptyData, slots);
			const queryResults = new QueryResults();
			queryResults.addResult(queryResult);
			return Promise.resolve(queryResults.getAPI());
		}

		_postProcess(rawDataResult, queryDefinition=null) {
			// In the future, to preserve the order of the post processors, a possible way is to define an array of
			// other post processor dependencies while register to query execution.
			this._invokeRegisteredPostProcessors(rawDataResult);
			QueryPostProcessHelper.postProcessMeasuresAsSeries(rawDataResult, this._getVisualization(), queryDefinition);
			//There are only 2 values for suppression 'rowsAndColumns' or 'none'
			const suppressionEnabled = this.content.getPropertyValue('suppression')!=='none';
			QueryPostProcessHelper.postProcessMultiEdgeMeasures(rawDataResult, queryDefinition, suppressionEnabled);
			QueryPostProcessHelper.postProcessAggregatedSort(rawDataResult, this._getVisualization());
			QueryPostProcessHelper.postProcessCustomSort(rawDataResult, this._getVisualization());
			QueryPostProcessHelper.postProcessOlapProperties(rawDataResult, this._getVisualization());
		}

		_invokeRegisteredPostProcessors(rawDataResult){
			this._queryPostProcessors.forEach(processor => processor.process(rawDataResult));
		}

		_buildGenericQueryTag(specList, resultList) {
			let fullKey = '';
			const INIT_TAG_ID = 100;
			let isLiveData = false;
			specList.forEach((spec, index) => {
				if (spec) {
					fullKey = fullKey.concat(JSON.stringify(spec));
				}
				const result = resultList[index];
				if (result.ETag) {
					fullKey = fullKey.concat(result.ETag);
				} else {
					isLiveData = true;
				}
			});
			if (_.isEmpty(this._queryKeyValuePair)) {
				this._queryKeyValuePair.key = fullKey;
				this._queryKeyValuePair.value = INIT_TAG_ID;
			} else if (this._queryKeyValuePair.key !== fullKey || isLiveData) {
				// Update the key value pair
				this._queryKeyValuePair.key = fullKey;
				this._queryKeyValuePair.value++;
			}
		}

		_buildQueryTag(queryList, resultList) {
			this._buildGenericQueryTag(
				queryList.map(({ spec }) => (spec)),
				resultList
			);
		}

		_buildQueryDefinitionTag(query, resultList) {
			this._buildGenericQueryTag(
				query.getSpecList(),
				resultList
			);
		}

		_canExecuteQuery() {
			// complete mapping is required for a complete query (unless forceQuery is enabled)
			// attempt to create the definition list without a complete mapping
			// will result the definition cache to become stale
			return this.dashboard.getConfiguration('forceQuery') === true || this._getVisualization().getSlots().isMappingComplete();
		}

		queryChanged() {
			if (!this._canExecuteQuery()) {
				return Promise.resolve(false);
			}
			return this._queryChanged();
		}

		_queryChanged() {
			// this._getQueryDefinitionList always returns a promise (even for v1)
			return this._getQueryDefinitionList(this.MAIN).then(queryDefinitions => {
				let queryChanged = false;
				if (queryDefinitions && queryDefinitions.length) {
					const query = this.features['Dashboard.QueryService2'].createQuery(queryDefinitions, 'v2');
					const querySpec = query && query.getSpecList();
					queryChanged = (JSON.stringify(querySpec) !== this._currentV2Specs);
					if (!queryChanged) {
						this._setDefinitionListCache();
					}
				} else {
					//Otherwise fall back to original QueryService
					const querySpecList = this._getQuerySpecList();
					if (!querySpecList) {
						// Should not happen
						this.logger.error('Could not build query spec');
					}

					if (!this._queryResults) {
						queryChanged = true;
					} else {
						queryChanged = _.some(this.currentSpecList, (currentSpec, idx) => {
							const querySpec = querySpecList[idx] && querySpecList[idx].spec;
							return JSON.stringify(querySpec) !== currentSpec;
						});
					}
				}
				return queryChanged;
			});
		}

		_getVisualization() {
			if (!this._visualization) {
				this._visualization = this.features['Visualization.internal'];
			}
			return this._visualization;
		}
	}

	return DataQueryExecution;
});
